import React from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Clock, 
  FileText, 
  BarChart3, 
  DollarSign, 
  TrendingUp, 
  Settings,
  ArrowRight,
  Calendar,
  Users,
  Target,
  Zap,
  Shield,
  Smartphone,
  HelpCircle
} from 'lucide-react';
import PageBreadcrumb from '../common/PageBreadcrumb';
import PageHeader from '../common/PageHeader';

const TimeTrackingLanding = () => {
  const navigate = useNavigate();

  const timeTrackingCards = [
    {
      id: 'time-entry',
      title: 'Time Entry',
      description: 'Log and manage time entries for projects and tasks with manual logging and timer functionality',
      icon: <Clock size={24} />,
      path: '/projects/time-tracking/entry',
      color: '#7C3AED',
      gradient: 'linear-gradient(135deg, #7C3AED 0%, #A855F7 100%)',
      features: ['Manual logging', 'Timer functionality', 'Bulk entry', 'Quick actions']
    },
    {
      id: 'timesheets',
      title: 'Timesheets',
      description: 'Manage weekly and monthly timesheets with approval workflows and team collaboration',
      icon: <FileText size={24} />,
      path: '/projects/time-tracking/timesheets',
      color: '#059669',
      gradient: 'linear-gradient(135deg, #059669 0%, #10B981 100%)',
      features: ['Weekly timesheets', 'Approval workflow', 'Team collaboration', 'Export options']
    },
    {
      id: 'time-reports',
      title: 'Time Reports',
      description: 'Generate comprehensive time tracking reports and analytics for projects and resources',
      icon: <BarChart3 size={24} />,
      path: '/projects/time-tracking/reports',
      color: '#DC2626',
      gradient: 'linear-gradient(135deg, #DC2626 0%, #EF4444 100%)',
      features: ['Project reports', 'Resource utilization', 'Performance metrics', 'Custom reports']
    },
    {
      id: 'billing-invoicing',
      title: 'Billing & Invoicing',
      description: 'Track billable time and generate client invoices with automated billing workflows',
      icon: <DollarSign size={24} />,
      path: '/projects/time-tracking/billing',
      color: '#F59E0B',
      gradient: 'linear-gradient(135deg, #F59E0B 0%, #FBBF24 100%)',
      features: ['Billable tracking', 'Invoice generation', 'Rate management', 'Client billing']
    },
    {
      id: 'time-analytics',
      title: 'Time Analytics',
      description: 'Advanced analytics and insights for productivity, trends, and performance optimization',
      icon: <TrendingUp size={24} />,
      path: '/projects/time-tracking/analytics',
      color: '#8B5CF6',
      gradient: 'linear-gradient(135deg, #8B5CF6 0%, #A78BFA 100%)',
      features: ['Productivity metrics', 'Trend analysis', 'Predictive insights', 'Performance dashboards']
    },
    {
      id: 'time-reports',
      title: 'Time Reports',
      description: 'Generate comprehensive time tracking reports and analytics',
      icon: <FileText size={24} />,
      path: '/projects/time-tracking/reports',
      color: '#0891B2',
      gradient: 'linear-gradient(135deg, #0891B2 0%, #06B6D4 100%)',
      features: ['Dashboard reports', 'Productivity analysis', 'Billing reports', 'Custom reports']
    },
    {
      id: 'time-settings',
      title: 'Time Settings',
      description: 'Configure time tracking policies, workflows, and integration settings',
      icon: <Settings size={24} />,
      path: '/projects/time-tracking/settings',
      color: '#6B7280',
      gradient: 'linear-gradient(135deg, #6B7280 0%, #9CA3AF 100%)',
      features: ['Workflow settings', 'Integration config', 'User preferences', 'Policy management']
    }
  ];

  const handleCardClick = (path) => {
    navigate(path);
  };

  // Handle help button click
  const handleHelpClick = () => {
    const helpUrl = '/help/time-tracking';
    const helpWindow = window.open(
      helpUrl,
      'helpWindow',
      'width=1200,height=800,scrollbars=yes,resizable=yes,menubar=no,toolbar=no,location=no,status=no'
    );
    if (helpWindow) {
      helpWindow.focus();
    }
  };

  // Define action buttons for PageHeader
  const headerActions = [
    {
      label: '', // Empty label for icon-only square button
      icon: <HelpCircle size={18} />,
      onClick: handleHelpClick,
      variant: 'outline',
      tooltip: 'Help (Press ? for keyboard shortcut)'
    }
  ];

  return (
    <>
      {/* Breadcrumb - Outside page-container to align with sidebar */}
      <PageBreadcrumb />
      {/* Page Header - Outside page-container to align with sidebar */}
      <PageHeader 
        title="Time Tracking"
        description="Comprehensive time management and tracking for projects, tasks, and team productivity"
        actions={headerActions}
      />
      <div className="page-container">
      {/* Time Tracking Modules */}
      <div 
        className="grid-modern-3"
        style={{
          maxWidth: '1200px',
          margin: '0 auto',
          width: '100%'
        }}
      >
              {timeTrackingCards.map((card) => (
                <div
                  key={card.id}
                  className="asset-landing-card"
                  onClick={() => handleCardClick(card.path)}
                >
                  {/* Top Accent Line */}
                  <div
                    style={{
                      height: '4px',
                      background: `var(--primary-color)`,
                      width: '100%'
                    }}
                  />
                  
                  {/* Card Content */}
                  <div style={{ padding: '24px' }}>
                    {/* Icon and Title */}
                    <div className="d-flex align-items-center mb-3">
                      <div
                        style={{
                          width: '40px',
                          height: '40px',
                          borderRadius: '10px',
                          background: '#D6EFFF',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          marginRight: '16px'
                        }}
                      >
                        <div style={{ color: '#2D7DD2' }} className="asset-card-icon">
                          {card.icon}
                        </div>
                      </div>
                      <div className="flex-grow-1">
                        <h4 style={{ 
                          fontSize: '18px', 
                          fontWeight: '600', 
                          color: 'var(--text-primary)',
                          margin: '0',
                          fontFamily: 'var(--font-family)'
                        }}>
                          {card.title}
                        </h4>
                      </div>
                      <ArrowRight 
                        size={20} 
                        className="asset-card-arrow"
                      />
                    </div>

                    {/* Description */}
                    <p style={{ 
                      fontSize: '13px', 
                      color: 'var(--text-secondary)',
                      margin: '0 0 12px 0',
                      lineHeight: '1.4',
                      fontFamily: 'var(--font-family)'
                    }}>
                      {card.description}
                    </p>

                    {/* Key Features */}
                    <div style={{ marginBottom: '16px' }}>
                      {card.features.map((feature, index) => (
                        <span
                          key={index}
                          style={{
                            display: 'inline-block',
                            backgroundColor: '#f3f4f6',
                            color: '#374151',
                            fontSize: '12px',
                            padding: '4px 8px',
                            borderRadius: '6px',
                            marginRight: '6px',
                            marginBottom: '6px',
                            fontFamily: 'var(--font-family)'
                          }}
                        >
                          {feature}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
      </div>

      {/* Time Tracking Overview Stats */}
      <div className="page-content">
        <div className="card">
          <div className="card-header">
            <h3 className="text-modern-title mb-0">Time Tracking Overview</h3>
            <p className="text-modern-description mb-0">
              Key metrics and insights for your time tracking activities
            </p>
          </div>
          <div className="card-body">
            <div className="grid-modern-4">
              <div className="stat-card">
                <div className="stat-icon" style={{ background: '#7C3AED20', color: '#7C3AED' }}>
                  <Clock size={24} />
                </div>
                <div className="stat-content">
                  <h4 className="stat-value">0</h4>
                  <p className="stat-label">Hours Logged Today</p>
                </div>
              </div>
              
              <div className="stat-card">
                <div className="stat-icon" style={{ background: '#05966920', color: '#059669' }}>
                  <Target size={24} />
                </div>
                <div className="stat-content">
                  <h4 className="stat-value">0</h4>
                  <p className="stat-label">Active Projects</p>
                </div>
              </div>
              
              <div className="stat-card">
                <div className="stat-icon" style={{ background: '#DC262620', color: '#DC2626' }}>
                  <Users size={24} />
                </div>
                <div className="stat-content">
                  <h4 className="stat-value">0</h4>
                  <p className="stat-label">Team Members</p>
                </div>
              </div>
              
              <div className="stat-card">
                <div className="stat-icon" style={{ background: '#F59E0B20', color: '#F59E0B' }}>
                  <DollarSign size={24} />
                </div>
                <div className="stat-content">
                  <h4 className="stat-value">$0</h4>
                  <p className="stat-label">Billable Amount</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      </div>
    </>
  );
};

export default TimeTrackingLanding;
